/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

/**
 * KNet embed mode, level 1 (hermes-agent-ui openspec add-kue-tool-workspace,
 * kue-embedded-services). Kue's tool workspace frames KnetDB with `?knet_embed=1`:
 * the app header (logo, org menu) and the account and org/workspace menus of the
 * top bar are hidden, since Kue's shell has them, and the left panel is the fixed
 * icon rail (PagePanels `rail`; openspec unify-embed-rail-and-signin). Editing is unchanged.
 *
 * Level 2 is Grist's own `style=singlePage`. This flag is deliberately not Grist's
 * `embed`, which also switches the document to read-only view mode.
 *
 * The flag is remembered for the frame's session, so in-app navigation keeps it
 * without rewriting links. A remembered flag only counts inside a frame.
 */

import { Observable } from "grainjs";

const KEY = "knet_embed";

export type KnetScreen = "desktop" | "phone" | "tablet";

/** The screen class from the shell's `knet:layout` message, or null before the first one.
 *  A phone is the layout to follow, never the frame width: a tool beside Kue's chat on a
 *  desktop can be narrow without being a phone (knet-kue openspec responsive-kue-layout). */
export function parseKnetScreen(data: unknown): KnetScreen | null {
  if (typeof data !== "object" || data === null || (data as { type?: unknown }).type !== "knet:layout") {
    return null;
  }
  const screen = (data as { screen?: unknown }).screen;
  return screen === "phone" || screen === "tablet" || screen === "desktop" ? screen : null;
}

/** Whether the left pane is Kue's fixed rail: embedded, unless the shell says phone. Before the
 *  first `knet:layout` (null) the tablet layout, the rail, applies. */
export function knetRail(embedded: boolean, screen: KnetScreen | null): boolean {
  return embedded && screen !== "phone";
}

let screenObs: Observable<KnetScreen | null> | null = null;

/** The shell's screen class, kept current from its `knet:layout` messages. */
export function knetScreenObs(): Observable<KnetScreen | null> {
  if (!screenObs) {
    const obs = Observable.create<KnetScreen | null>(null, null);
    screenObs = obs;
    if (typeof window !== "undefined" && window.self !== window.top) {
      window.addEventListener("message", (event) => {
        const screen = event.source === window.parent ? parseKnetScreen(event.data) : null;
        if (screen) { obs.set(screen); }
      });
    }
  }
  return screenObs;
}

// Listen from the start: the shell posts the class when the frame loads, before any pane exists.
knetScreenObs();

export function isKnetEmbed(): boolean {
  let result = false;
  const fromUrl = new URLSearchParams(window.location.search).get(KEY) === "1";
  try {
    if (fromUrl) {
      window.sessionStorage.setItem(KEY, "1");
      result = true;
    }
    else {
      result = window.self !== window.top && window.sessionStorage.getItem(KEY) === "1";
    }
  }
  catch {
    result = fromUrl;
  }
  if (result) {
    installKnetEmbedNavigationInterceptor();
  }
  return result;
}

/**
 * Opens a URL as a new tab inside the Kue shell if currently running embedded inside Kue.
 * Returns true if the message was posted to Kue parent window.
 */
export function openInKueTab(url?: string, title?: string): boolean {
  if (!url || typeof window === "undefined" || window.self === window.top) {
    return false;
  }
  try {
    const fullUrl = new URL(url, window.location.href).href;
    window.parent.postMessage({
      type: "knet:open",
      url: fullUrl,
      title,
      copy: true,
      newTab: true,
    }, "*");
    return true;
  }
  catch {
    return false;
  }
}

/**
 * KnetDB's own origin, or null when it has none to name. Kue's shell shares KnetDB's host, so a message addressed
 * here reaches Kue and no other page (openspec readable-own-origin).
 */
export function ownOrigin(): string | null {
  if (typeof window === "undefined") { return null; }
  const origin = window.location?.origin;
  return origin && origin !== "null" ? origin : null;
}

/**
 * Broadcasts copilot readable state to the parent Kue Shell, addressed to KnetDB's own origin (never "*").
 */
export function emitCopilotState(name: string, value: unknown, targetWindow?: Window | null,
  targetOrigin: string | null = ownOrigin()): boolean {
  const win = targetWindow ?? (typeof window !== "undefined" && window.self !== window.top ? window.parent : null);
  if (!win || !targetOrigin) {
    return false;
  }
  try {
    win.postMessage({
      type: "knet:copilot-state",
      name: name.trim(),
      value,
    }, targetOrigin);
    return true;
  }
  catch {
    return false;
  }
}

export interface CopilotActionCall {
  type: "knet:copilot-action-call";
  callId: string;
  name: string;
  args: Record<string, unknown>;
}

export interface CopilotActionResult {
  type: "knet:copilot-action-result";
  callId: string;
  result?: unknown;
  error?: string;
}

export type CopilotActionHandler = (name: string, args: Record<string, unknown>) => Promise<unknown>;

let activeActionHandler: CopilotActionHandler | null = null;

/**
 * Registers the active document action handler to execute incoming copilot actions.
 */
export function registerCopilotActionHandler(handler: CopilotActionHandler | null): void {
  activeActionHandler = handler;
}

/**
 * Returns the currently registered action handler (for testing and introspection).
 */
export function getActiveCopilotActionHandler(): CopilotActionHandler | null {
  return activeActionHandler;
}

/**
 * Emits an action execution result back to the parent Kue window.
 */
export function emitCopilotActionResult(
  callId: string,
  result?: unknown,
  error?: string,
  targetWindow?: Window | null,
  targetOrigin: string | null = ownOrigin(),
): boolean {
  const win = targetWindow ?? (typeof window !== "undefined" && window.self !== window.top ? window.parent : null);
  if (!win || !targetOrigin) {
    return false;
  }
  try {
    win.postMessage({
      type: "knet:copilot-action-result",
      callId,
      result,
      error,
    }, targetOrigin);
    return true;
  }
  catch {
    return false;
  }
}

/**
 * Type guard for incoming copilot action calls from the shell.
 */
export function isCopilotActionCall(data: unknown): data is CopilotActionCall {
  if (!data || typeof data !== "object") { return false; }
  const msg = data as Record<string, unknown>;
  return (
    msg.type === "knet:copilot-action-call" &&
    typeof msg.callId === "string" && msg.callId.trim().length > 0 &&
    typeof msg.name === "string" && msg.name.trim().length > 0 &&
    typeof msg.args === "object" && msg.args !== null
  );
}

/**
 * Dispatches an incoming action call to the active handler and sends back the result.
 */
export async function dispatchCopilotActionCall(
  callMsg: CopilotActionCall,
  targetWindow?: Window | null,
  targetOrigin: string | null = ownOrigin(),
): Promise<boolean> {
  if (!activeActionHandler) {
    return emitCopilotActionResult(
      callMsg.callId,
      undefined,
      "No active document or action handler available in KnetDB",
      targetWindow,
      targetOrigin,
    );
  }

  try {
    const result = await activeActionHandler(callMsg.name, callMsg.args);
    return emitCopilotActionResult(callMsg.callId, result, undefined, targetWindow, targetOrigin);
  }
  catch (err: any) {
    return emitCopilotActionResult(callMsg.callId, undefined, err?.message || String(err), targetWindow, targetOrigin);
  }
}

/**
 * Installs the window message listener for knet:copilot-action-call.
 */
export function installCopilotActionListener(win: Window = window): void {
  if (typeof win === "undefined" || (win as any)._knetActionListenerInstalled) {
    return;
  }
  (win as any)._knetActionListenerInstalled = true;

  win.addEventListener("message", async (event: MessageEvent) => {
    // Only the Kue shell, which shares KnetDB's host, may drive the document (openspec db-copilot-actions 2.1).
    if (!acceptsActionCallFrom(win, event)) {
      return;
    }

    if (isCopilotActionCall(event.data)) {
      await dispatchCopilotActionCall(event.data, win.parent, event.origin);
    }
  });
}

/** True for a message from the embedding parent with KnetDB's own origin. */
export function acceptsActionCallFrom(win: Pick<Window, "self" | "top" | "parent" | "location">,
  event: Pick<MessageEvent, "source" | "origin">): boolean {
  return win.self !== win.top && event.source === win.parent && event.origin === win.location.origin;
}

/** What KnetDB's action handler needs from an open document; GristDoc in the app, a fake in tests. */
export interface KnetDbActionTarget {
  hasTable(tableId: string): boolean;
  applyUserActions(actions: unknown[][]): Promise<unknown>;
  activeSectionId(): number;
  /** The row the cursor is on, when it is on a saved row. */
  currentRowId(): number | undefined;
  /** The position of a column (by id or label, any case) in a section's fields. */
  fieldIndexOf(sectionId: number, column: string): number | undefined;
  /** Moves the cursor; false when it could not. */
  moveCursor(pos: { rowId: number; sectionId: number; fieldIndex?: number }): Promise<boolean>;
}

/** One column of the open table, as the agent sees it. */
export interface OpenTableColumn { id: string; label: string; type: string; formula?: boolean }

/** The openTable readable: the open table's schema and where the cursor is (openspec db-copilot-actions 2.5). */
export interface OpenTable {
  tableId: string;
  sectionId: number;
  columns: OpenTableColumn[];
  cursor: { rowId?: number; column?: string };
}

/**
 * Builds the openTable readable from the open section's fields and the cursor; null when no table is open.
 * Pure, so it is tested without a document.
 */
export function describeOpenTable(tableId: string | undefined, sectionId: number | undefined,
  columns: OpenTableColumn[], cursor: { rowId?: unknown; fieldIndex?: number } | undefined): OpenTable | null {
  if (!tableId || !sectionId) { return null; }
  const rowId = typeof cursor?.rowId === "number" ? cursor.rowId : undefined;
  const column = cursor?.fieldIndex === undefined ? undefined : columns[cursor.fieldIndex]?.label;
  return { tableId, sectionId, columns, cursor: { ...(rowId ? { rowId } : {}), ...(column ? { column } : {}) } };
}

/** The UI actions KnetDB accepts, published as the uiActions readable (openspec db-copilot-actions 2.3). */
export const KNETDB_UI_ACTIONS = [
  {
    name: "setCursor",
    description: "Move the cursor to a row and/or a column so the user sees it",
    params: {
      rowId: "number (optional: the current row)",
      column: "column id or label (optional), e.g. Country",
      sectionId: "number (optional: the open section)",
    },
  },
  {
    name: "updateRecord",
    description: "Edit one visible record (undoable); bulk or background changes go through the MCP connector",
    params: { tableId: "string", rowId: "number", fields: 'JSON array of ["Column", value] pairs', sectionId: "number (optional)" },
    requiresApproval: true,
  },
  {
    name: "addRecord",
    description: "Add one visible record (undoable); bulk or background changes go through the MCP connector",
    params: { tableId: "string", fields: 'JSON array of ["Column", value] pairs' },
    requiresApproval: true,
  },
];

function positiveInt(args: Record<string, unknown>, key: string, optional = false): number | undefined {
  const v = args[key];
  if (v === undefined && optional) { return undefined; }
  if (typeof v !== "number" || !Number.isInteger(v) || v <= 0) {
    throw new Error(`${key} must be a positive integer`);
  }
  return v;
}

function tableIdOf(target: KnetDbActionTarget, args: Record<string, unknown>): string {
  const tableId = args.tableId;
  if (typeof tableId !== "string" || !tableId.trim()) {
    throw new Error("tableId must be a table name");
  }
  if (!target.hasTable(tableId)) {
    throw new Error(`No table "${tableId}"`);
  }
  return tableId;
}

function fieldsOf(args: Record<string, unknown>): Record<string, unknown> {
  const f = args.fields;
  if (Array.isArray(f) && f.every(p => Array.isArray(p) && p.length === 2 && typeof p[0] === "string" && p[0])) {
    return Object.fromEntries(f as [string, unknown][]);
  }
  if (f && typeof f === "object" && !Array.isArray(f) && Object.keys(f).length > 0) {
    return f as Record<string, unknown>;
  }
  throw new Error("fields must be an object or [column, value] pairs");
}

/**
 * KnetDB's action handler on one open document (openspec db-copilot-actions 2.2). Every argument is checked
 * before the document is touched, and a cursor that does not move fails the call.
 */
export function createKnetDbActionHandler(target: KnetDbActionTarget): CopilotActionHandler {
  const sectionOf = (sectionId: number | undefined) => {
    const section = sectionId ?? target.activeSectionId();
    if (!section) {
      throw new Error("No open section to move the cursor in");
    }
    return section;
  };
  const moveTo = async (rowId: number, sectionId: number | undefined, fieldIndex?: number) => {
    const section = sectionOf(sectionId);
    if (!await target.moveCursor({ rowId, sectionId: section, fieldIndex })) {
      throw new Error(`Could not move to row ${rowId} in section ${section}`);
    }
  };
  return async (name, args) => {
    switch (name) {
      case "setCursor": {
        const sectionId = positiveInt(args, "sectionId", true);
        let fieldIndex = args.fieldIndex === undefined ? undefined : positiveIntOrZero(args, "fieldIndex");
        if (args.column !== undefined) {
          if (typeof args.column !== "string" || !args.column.trim()) {
            throw new Error("column must be a column id or label");
          }
          fieldIndex = target.fieldIndexOf(sectionOf(sectionId), args.column.trim());
          if (fieldIndex === undefined) {
            throw new Error(`No column "${args.column}" in the open section`);
          }
        }
        const rowId = positiveInt(args, "rowId", true) ?? target.currentRowId();
        if (rowId === undefined) {
          throw new Error("rowId is required when the cursor is not on a row");
        }
        await moveTo(rowId, sectionId, fieldIndex);
        return { success: true, rowId, ...(fieldIndex === undefined ? {} : { fieldIndex }) };
      }
      case "updateRecord": {
        const tableId = tableIdOf(target, args);
        const rowId = positiveInt(args, "rowId")!;
        const sectionId = positiveInt(args, "sectionId", true);
        const fields = fieldsOf(args);
        await target.applyUserActions([["UpdateRecord", tableId, rowId, fields]]);
        await moveTo(rowId, sectionId).catch(() => undefined);
        return { success: true, tableId, rowId };
      }
      case "addRecord": {
        const tableId = tableIdOf(target, args);
        const fields = fieldsOf(args);
        const result = await target.applyUserActions([["AddRecord", tableId, null, fields]]);
        return { success: true, tableId, result };
      }
      default:
        throw new Error(`Unsupported action "${name}" for KnetDB`);
    }
  };
}

function positiveIntOrZero(args: Record<string, unknown>, key: string): number {
  const v = args[key];
  if (typeof v !== "number" || !Number.isInteger(v) || v < 0) {
    throw new Error(`${key} must be a whole number`);
  }
  return v;
}

// Automatically install listener when running embedded
if (typeof window !== "undefined" && window.self !== window.top) {
  installCopilotActionListener(window);
}

/**
 * Installs global click and window.open interceptors when embedded inside Kue.
 * Any link with target="_blank" or imperative window.open will be routed to Kue's
 * tab bar instead of opening a new top-level browser tab/window.
 */
export function installKnetEmbedNavigationInterceptor(): void {
  if (typeof window === "undefined" || (window as any)._knetNavInterceptorInstalled) {
    return;
  }
  (window as any)._knetNavInterceptorInstalled = true;

  // Intercept window.open
  const origOpen = window.open;
  window.open = function(url?: string | URL, target?: string, features?: string): Window | null {
    if (url && (target === "_blank" || !target)) {
      if (openInKueTab(url.toString())) {
        return null;
      }
    }
    return origOpen.call(window, url, target, features);
  };

  // Intercept clicks on <a target="_blank">
  window.addEventListener(
    "click",
    (ev: MouseEvent) => {
      if (ev.defaultPrevented) { return; }
      const anchor = (ev.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) { return; }

      if (anchor.hasAttribute("download")) { return; }

      const target = anchor.getAttribute("target");
      const href = anchor.getAttribute("href");
      if (!href || href === "#" || href.startsWith("javascript:") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }

      if (target === "_blank") {
        ev.preventDefault();
        ev.stopPropagation();
        const title = anchor.title || anchor.textContent?.trim() || undefined;
        openInKueTab(href, title);
      }
    },
    true, // Capture phase ensures we intercept before default handling
  );
}
